<template>
    <transition name="fade">
        <div
            id="quiz-generate-bg"
            :class="{ 'modal d-block fixed pin-t pin-l w-full h-full': true, show: showModal, close: !showModal }"
        >
            <div
                id="generate-bg-box"
                :class="{
                    'modal flex items-center d-flex justify-content-center align-items-center p-2 md:p-0': true,
                    show: showContent,
                    close: showContent === false
                }"
            >
                <div @click.stop="closeModal()" class="bg-backdrop absolute w-full h-full"></div>
                <div id="generate-bg-content" class="modal-content w-full h-full flex flex-col relative">
                    <div
                        class="modal-header flex justify-between items-center d-flex justify-content-between align-items-center"
                    >
                        <h2 class="m-0 shadow-text-none">
                            {{ translate('modal_ai_background_header', 'Generate Background with AI') }}
                        </h2>
                        <i
                            @click.stop="closeModal()"
                            class="fas text-grey fa-times text-xl shadow-text-none cursor-pointer"
                        ></i>
                    </div>
                    <div class="modal-body flex justify-center items-center p-4 z-10 overflow-y-auto">
                        <img
                            v-if="imageUrl"
                            class="w-full w-100"
                            style="background-position: center; object-fit: cover"
                            :src="imageUrl"
                        />
                    </div>
                    <div class="modal-footer flex flex-col md:flex-row justify-center items-center p-4">
                        <XButton
                            @click.stop="backend ? genBgBackend() : genBg()"
                            :disabled="generating"
                            bg-color="#17a2b8"
                            font-weight="400"
                            type="button"
                            class="w-full w-100 m-0 shadow-text-none"
                        >
                            <i class="fas fa-magic"></i>
                            {{ translate('modal_ai_background_button_generate', 'Generate', { limit: limitAiImage }) }}
                        </XButton>
                        <XButton
                            @click.stop="chooseImage"
                            :disabled="generating"
                            type="button"
                            class="w-full w-100 m-0 system-button-color system-button-text-color"
                        >
                            {{ translate('modal_ai_background_button_choose', 'Choose Background') }}
                        </XButton>
                    </div>
                </div>
            </div>
            <RobotLoader
                v-if="generating"
                :show-progress-bar="true"
                :progress="currentProgress"
                :total-progress="1"
                :error-progress="errorProgress"
            />
            <RobotSorryAlert
                v-if="showSorryAlert"
                @retry="backend ? genBgBackend() : genBg()"
                @close="closeAlert()"
                :message="translate('modal_ai_background_message_error', 'Something wrong try again later')"
                class="show d-block"
            />

            <UpgradeAiQuestionModal
                v-if="showUpgrade"
                @success="onSuccess()"
                @close="closeAlert()"
                ref="upgradeAiQuestion"
            />
        </div>
    </transition>
</template>

<script>
import { load } from 'recaptcha-v3'
import { mapState } from 'vuex'
import XButton from '../../components/v2/XButton'
import RobotLoader from '../../components/quiz-creator/question-section/RobotLoader'
import RobotSorryAlert from '../../components/quiz-creator/question-section/RobotSorryAlert'

let Request, UpgradeAiQuestionModal
let getToken = () => {}

if (typeof process.env.VUE_APP_IS_BACKEND === 'undefined') {
    Request = require('@/lib/request.js').default
    UpgradeAiQuestionModal = require('@/components/UpgradeAiQuestionModal.vue').default
    getToken = require('@/lib/user.js').getToken
}

export default {
    components: {
        XButton,
        RobotLoader,
        RobotSorryAlert,
        UpgradeAiQuestionModal
    },

    props: {
        image: {
            type: String,
            required: true
        }
    },

    data() {
        return {
            showModal: true,
            showContent: null,
            showSorryAlert: false,
            showUpgrade: false,
            generating: false,
            currentProgress: 0,
            errorProgress: 0,
            imageUrl: '',
            limitAiImage: null
        }
    },

    computed: {
        ...mapState({
            quiz: state => state.question
        }),

        backend() {
            if (typeof process.env.VUE_APP_IS_BACKEND !== 'undefined') {
                return true
            }

            return false
        }
    },

    methods: {
        popup() {
            let popup

            try {
                popup = require('@/lib/popup.js').default
            } catch {
                if (typeof window.shout != 'undefined') {
                    popup = window.shout
                    popup.alert = popup.error
                }
            }

            return popup
        },

        closeModal() {
            const acceptCallback = () => {
                this.showModal = false
                this.showUpgrade = false

                if (this.showContent) {
                    this.showContent = false
                }

                setTimeout(() => {
                    this.$emit('close')
                }, 300)
            }

            const msg = this.translate(
                'setting_confirm_close',
                'Are you certain you want to close? Any unsaved changes will be lost'
            )

            if (this.backend) {
                this.$vueConfirm.confirm(
                    {
                        message: msg,
                        button: {
                            no: 'No',
                            yes: 'Yes'
                        }
                    },
                    function (confirm) {
                        if (confirm) {
                            if (acceptCallback) {
                                acceptCallback()
                            }
                        }
                    }
                )
            } else {
                this.popup().confirm(msg, () => {
                    if (acceptCallback) {
                        acceptCallback()
                    }
                })
            }
        },

        closeAlert() {
            this.showSorryAlert = false
            this.showUpgrade = false

            if (!this.showContent) {
                this.showModal = false

                if (this.showContent) {
                    this.showContent = false
                }

                if (this.backend) {
                    this.$emit('close')
                } else {
                    setTimeout(() => {
                        this.$emit('close')
                    }, 300)
                }
            }
        },

        translate(key, msg, options) {
            if (typeof process.env.VUE_APP_IS_BACKEND !== 'undefined') {
                return msg
            }

            return this.$t(key, options)
        },

        chooseImage() {
            this.$emit('image-selected', this.imageUrl)

            this.showModal = false
            this.showUpgrade = false

            if (this.showContent) {
                this.showContent = false
            }

            setTimeout(() => {
                this.$emit('close')
            }, 300)
        },

        async getLimitAiImage() {
            const { limit } = await this.$store.dispatch('getLimitAiImage')

            this.limitAiImage = limit
        },

        async setLimitAiImage() {
            const { left } = await this.$store.dispatch('setAiImageByUser')

            this.limitAiImage = left
        },

        async onSuccess() {
            await this.getLimitAiImage()

            if (!this.showContent) {
                this.genBg()
            }
        },

        async genBg() {
            if (this.generating) return

            if (this.limitAiImage === null) {
                await this.getLimitAiImage()
            }

            if (this.limitAiImage <= 0) {
                this.showUpgrade = true
                return
            }

            this.currentProgress = 0
            this.errorProgress = []

            this.showSorryAlert = false

            this.generating = true

            try {
                const url = `${process.env.VUE_APP_CHATGPT_BASE_URL}/generate-background`

                const recaptcha = await load(process.env.VUE_APP_RECAPTCHA_SITE_KEY, {
                    useRecaptchaNet: true,
                    autoHideBadge: true
                })

                const token = await recaptcha.execute('validate_captcha', { action: 'validate_captcha' })
                const request = new Request({
                    headers: {
                        Authorization: `Bearer ${getToken()}`
                    }
                })

                const generate = await request.post(url, {
                    captcha_token: token,
                    image: this.image
                })

                if (generate.status === 200) {
                    this.currentProgress++
                    this.imageUrl = generate.data.data.url

                    this.setLimitAiImage()

                    this.showContent = true
                }
            } catch {
                this.errorProgress = [1]
                this.showSorryAlert = true
            } finally {
                this.generating = false
            }
        },

        async genBgBackend() {
            if (this.generating) return

            this.currentProgress = 0
            this.errorProgress = []

            this.showSorryAlert = false

            this.generating = true

            try {
                const generate = await this.$store.dispatch('quiz/generateAiBackground', {
                    image: this.image
                })

                if (generate.status === 200) {
                    this.currentProgress++
                    this.imageUrl = generate.data.data.url
                    this.showContent = true
                }
            } catch {
                this.errorProgress = [1]
                this.showSorryAlert = true
            } finally {
                this.generating = false
            }
        }
    }
}
</script>

<style lang="scss">
#quiz-generate-bg {
    background-color: rgba(0, 0, 0, 0.5);

    &.fade-enter-active,
    &.fade-leave-active {
        transition: opacity 0.3s;
    }

    &.fade-enter,
    &.fade-leave-to {
        opacity: 0;
    }

    .modal.close {
        opacity: 0;
    }

    .robot-backdrop {
        background-color: rgba(0, 0, 0, 0.5);
    }

    #generate-bg-box {
        background-color: transparent !important;
        transition: opacity 0.3s;
        opacity: 0;

        &.show {
            opacity: 1;
            transform: translateY(0px);
        }

        .bg-backdrop {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
        }

        #generate-bg-content {
            width: 100%;
            max-width: 620px;
            height: auto !important;

            .modal-header {
                padding: 1rem 1rem 0rem 1rem;
                border-bottom: none !important;

                h2 {
                    font-size: 20px;
                }

                i {
                    color: var(--dark-color);
                    font-size: 20px !important;
                }
            }

            .modal-body {
                img {
                    aspect-ratio: 1 / 1;
                    border: 1px solid #000;
                    border-radius: 6px;
                }
            }

            .modal-footer {
                gap: 1rem;
                padding: 0rem 1rem 1rem 1rem;
                border-top: none !important;

                .x-button {
                    @media screen and (min-width: 768px) {
                        flex: 1;
                    }
                }
            }
        }
    }
}
</style>